import { type PropsWithChildren } from 'react';

import {
	createContainer,
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type OverrideContainerComponent,
	type StoreActionApi,
} from 'react-sweet-state';

import { teamsClient } from '@atlaskit/teams-client';

import { promiseWithDefault } from '../../common/utils/promise';
import { type AppName } from '../app-config-context/types';
import { type FeatureFlag, getSettingsFromStorage } from '../feature-flags';

import { mapAppNameToProduct } from './utils';

export const BROWSE_USER_SETTINGS_KEY = 'internal.browse-users-allowed';

type BrowseUserPerms = { [BROWSE_USER_SETTINGS_KEY]: boolean };

type FlagsWithBrowserUserPerm = BrowseUserPerms;

export type State = {
	hasLoadedSettings: boolean;
	hasErrorOnFetchSettings: boolean;
	hasBrowseUsersPermission: boolean;
	settings: { [k: string]: boolean | null };
	isFetching: boolean;
};
type StoreApi = StoreActionApi<State>;

const actions = {
	/**
	 * Fetches bup settings from pf-directory/legion service
	 * @param product - Product name.
	 * @param cloudId - Cloud Id.
	 * @private
	 * @deprecated Use getSettingsNext instead.
	 */
	getSettings:
		(product?: string, cloudId?: string) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			if (getState().isFetching || getState().hasLoadedSettings) {
				return;
			}
			setState({ isFetching: true });
			try {
				const directorySettingPromise = cloudId
					? teamsClient.querySettings(cloudId, product)
					: Promise.resolve({ [BROWSE_USER_SETTINGS_KEY]: true });

				return await promiseWithDefault<Partial<BrowseUserPerms>>(directorySettingPromise, {
					[BROWSE_USER_SETTINGS_KEY]: false,
				}).then(({ value: browseUsersEnabled, success: settingsSuccess }) => {
					const settingsFromRequests: FlagsWithBrowserUserPerm = {
						// If settings cannot be accessed, default to permission denied.
						[BROWSE_USER_SETTINGS_KEY]: false,
						...browseUsersEnabled,
					};

					// Fallback as needed to compute effective flag values
					const finalSettings = {
						...(getSettingsFromStorage() || {}),
						...settingsFromRequests,
					};

					setState({
						hasLoadedSettings: true,
						hasErrorOnFetchSettings: !settingsSuccess,
						hasBrowseUsersPermission: !!finalSettings[BROWSE_USER_SETTINGS_KEY],
						settings: finalSettings,
					});
				});
			} catch {
				setState({ hasErrorOnFetchSettings: true, hasLoadedSettings: true });
			} finally {
				setState({ isFetching: false });
			}
		},

	/**
	 * Fetches bup settings from pf-directory/legion service, but with a different method signature.
	 * @param product - Product name.
	 * @param cloudId - Cloud Id.
	 */
	getSettingsNext:
		({
			appName,
			cloudId,
			pushOperationalEvent,
		}: {
			appName: AppName;
			cloudId?: string;
			pushOperationalEvent: (succeeded: boolean) => void;
		}) =>
		async ({ getState, setState }: StoreApi): Promise<void> => {
			if (getState().isFetching || getState().hasLoadedSettings) {
				return;
			}
			setState({ isFetching: true });
			try {
				const product = appName ? mapAppNameToProduct[appName] : undefined;

				const directorySettingPromise = cloudId
					? teamsClient.querySettings(cloudId, product)
					: Promise.resolve({ [BROWSE_USER_SETTINGS_KEY]: true });

				return await promiseWithDefault<Partial<BrowseUserPerms>>(directorySettingPromise, {
					[BROWSE_USER_SETTINGS_KEY]: false,
				}).then(({ value: browseUsersEnabled, success: settingsSuccess }) => {
					const settingsFromRequests: FlagsWithBrowserUserPerm = {
						// If settings cannot be accessed, default to permission denied.
						[BROWSE_USER_SETTINGS_KEY]: false,
						...browseUsersEnabled,
					};

					// Fallback as needed to compute effective flag values
					const finalSettings = {
						...(getSettingsFromStorage() || {}),
						...settingsFromRequests,
					};

					pushOperationalEvent(settingsSuccess);
					setState({
						hasLoadedSettings: true,
						hasErrorOnFetchSettings: !settingsSuccess,
						hasBrowseUsersPermission: !!finalSettings[BROWSE_USER_SETTINGS_KEY],
						settings: finalSettings,
					});
				});
			} catch {
				pushOperationalEvent(false);
				setState({ hasErrorOnFetchSettings: true, hasLoadedSettings: true });
			} finally {
				setState({ isFetching: false });
			}
		},
};

type Actions = typeof actions;

const initialState: State = {
	hasLoadedSettings: false,
	hasErrorOnFetchSettings: false,
	hasBrowseUsersPermission: false,
	isFetching: false,
	settings: {
		[BROWSE_USER_SETTINGS_KEY]: false,
		...(getSettingsFromStorage() || {}),
	},
};

/**
 * Manage states of settings
 */
const Store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'Settings',
});

export const SettingsContainer: OverrideContainerComponent<PropsWithChildren<Partial<State>>> =
	createContainer<State, Actions, PropsWithChildren<Partial<State>>>(Store, {
		onInit:
			() =>
			({ setState }, state) => {
				setState(state);
			},
	});

export const useSettings: HookFunction<
	State,
	BoundActions<
		State,
		{
			/**
			 * Fetches bup settings from pf-directory/legion service
			 * @param product - Product name.
			 * @param cloudId - Cloud Id.
			 * @private
			 * @deprecated Use getSettingsNext instead.
			 */
			getSettings: (
				product?: string,
				cloudId?: string,
			) => ({ getState, setState }: StoreApi) => Promise<void>;
			/**
			 * Fetches bup settings from pf-directory/legion service, but with a different method signature.
			 * @param product - Product name.
			 * @param cloudId - Cloud Id.
			 */
			getSettingsNext: ({
				appName,
				cloudId,
				pushOperationalEvent,
			}: {
				appName: AppName;
				cloudId?: string;
				pushOperationalEvent: (succeeded: boolean) => void;
			}) => ({ getState, setState }: StoreApi) => Promise<void>;
		}
	>,
	void
> = createHook(Store);

/**
 * @deprecated As part of decommisioning pf-directoy
 */
export const useSettingsFeatureFlag = (flagName: FeatureFlag): boolean | null => {
	const [{ settings }] = useSettings();

	return settings[flagName];
};
